import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Download,
  FileText,
  History,
  Loader2,
  Receipt,
  Upload,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { farmGet, farmPatch, farmPost, STATO_FATTURA_LABELS } from "./shared";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FATTURE_APERTE = new Set(["pubblicata", "visualizzata", "da_pagare"]);

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function FatturazioneFarmacia() {
  const { token } = useFarmaciaAuth();
  const [searchParams] = useSearchParams();
  const [strutture, setStrutture] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [inviaEmail, setInviaEmail] = useState(true);
  const [allegaRiepilogo, setAllegaRiepilogo] = useState(false);
  const [numero, setNumero] = useState("");
  const [dataFattura, setDataFattura] = useState("");
  const [periodoDa, setPeriodoDa] = useState("");
  const [periodoA, setPeriodoA] = useState("");
  const [importo, setImporto] = useState("");
  const [scadenza, setScadenza] = useState("");
  const [mese, setMese] = useState("");
  const [statoUpload, setStatoUpload] = useState("da_pagare");
  const [note, setNote] = useState("");
  const [auditId, setAuditId] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [statoFilter, setStatoFilter] = useState(() => searchParams.get("stato") || "");
  const fileRef = useRef(null);

  useEffect(() => {
    setStatoFilter(searchParams.get("stato") || "");
  }, [searchParams]);

  const loadStrutture = useCallback(async () => {
    if (!token) return;
    try {
      const res = await farmGet("/farmacia/strutture", token, { lite: true });
      const list = res.items || [];
      setStrutture(list);
      if (!orgId && list[0]) setOrgId(list[0].organization_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore strutture");
    }
  }, [token, orgId]);

  const loadFatture = useCallback(async () => {
    if (!token || !orgId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await farmGet("/farmacia/fatture", token, { organization_id: orgId });
      setItems(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento fatture");
    } finally {
      setLoading(false);
    }
  }, [token, orgId]);

  useEffect(() => {
    loadStrutture();
  }, [loadStrutture]);

  useEffect(() => {
    loadFatture();
  }, [loadFatture]);

  const filteredItems = useMemo(() => {
    if (!statoFilter) return items;
    if (statoFilter === "aperte") {
      return items.filter((d) => {
        const st = d.stato_fattura;
        if (st) return FATTURE_APERTE.has(st);
        return d.pagata !== true && d.origine === "portale_farmacia";
      });
    }
    return items.filter((d) => (d.stato_fattura || "") === statoFilter);
  }, [items, statoFilter]);

  const upload = async (files) => {
    if (!orgId) {
      toast.error("Seleziona una struttura");
      return;
    }
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("organization_id", orgId);
      fd.append("invia_email", inviaEmail ? "true" : "false");
      fd.append("allega_riepilogo_extra", allegaRiepilogo ? "true" : "false");
      fd.append("stato_fattura", statoUpload);
      if (mese) fd.append("mese_riferimento", mese);
      if (numero.trim()) fd.append("numero_fattura", numero.trim());
      if (dataFattura) fd.append("data_fattura", dataFattura);
      if (periodoDa) fd.append("periodo_da", periodoDa);
      if (periodoA) fd.append("periodo_a", periodoA);
      if (importo !== "") fd.append("importo", String(importo).replace(",", "."));
      if (scadenza) fd.append("scadenza", scadenza);
      if (note.trim()) fd.append("note", note.trim());
      Array.from(files).forEach((f) => fd.append("files", f));
      const res = await farmPost("/farmacia/fatture", token, fd, true);
      const items = res.items || [];
      const n = res.count || items.length || 0;
      const splitN = items.filter((x) => x.split && !x.skipped_duplicate).length;
      const emailed = items.filter((x) => x.email_sent).length;
      toast.success(
        `${n} fattura/e caricate` +
          (splitN ? ` (scorporate da PDF unico)` : "") +
          (inviaEmail ? ` · email notifica: ${emailed ? "ok" : "non inviata"}` : ""),
      );
      await loadFatture();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setStato = async (doc, stato) => {
    try {
      const res = await farmPatch(`/farmacia/fatture/${doc.id}`, token, { stato_fattura: stato });
      setItems((prev) => prev.map((x) => (x.id === doc.id ? res.fattura : x)));
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    }
  };

  const saveMeta = async (doc, body) => {
    try {
      const res = await farmPatch(`/farmacia/fatture/${doc.id}`, token, body);
      setItems((prev) => prev.map((x) => (x.id === doc.id ? res.fattura : x)));
      setEditId(null);
      toast.success("Metadati salvati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito");
    }
  };

  const openAudit = async (id) => {
    if (auditId === id) {
      setAuditId(null);
      return;
    }
    setAuditId(id);
    setAuditLoading(true);
    try {
      const res = await farmGet(`/farmacia/fatture/${encodeURIComponent(id)}/audit`, token);
      setAudit(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit non disponibile");
      setAudit([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const downloadAuth = async (doc) => {
    try {
      const res = await fetch(`${API_URL}/api/farmacia/fatture/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "fattura.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Download fallito");
    }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="border-b px-4 py-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Upload className="h-4 w-4 text-emerald-600" />
            Carica fatture
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Building2 className="h-3.5 w-3.5" />
                Struttura
              </label>
              <select
                className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              >
                <option value="">Seleziona…</option>
                {strutture.map((s) => (
                  <option key={s.organization_id} value={s.organization_id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Numero</label>
              <Input className="mt-1" value={numero} onChange={(e) => setNumero(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Data fattura</label>
              <Input type="date" className="mt-1" value={dataFattura} onChange={(e) => setDataFattura(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Periodo da</label>
              <Input type="date" className="mt-1" value={periodoDa} onChange={(e) => setPeriodoDa(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Periodo a</label>
              <Input type="date" className="mt-1" value={periodoA} onChange={(e) => setPeriodoA(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Mese riferimento</label>
              <Input type="month" className="mt-1" value={mese} onChange={(e) => setMese(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Importo €</label>
              <Input className="mt-1" value={importo} onChange={(e) => setImporto(e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Scadenza</label>
              <Input type="date" className="mt-1" value={scadenza} onChange={(e) => setScadenza(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Stato iniziale</label>
              <select
                className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3"
                value={statoUpload}
                onChange={(e) => setStatoUpload(e.target.value)}
              >
                {Object.entries(STATO_FATTURA_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600">Note</label>
              <Input className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={inviaEmail} onChange={(e) => setInviaEmail(e.target.checked)} />
            <span>Invia email di notifica alla casella Affidya della struttura</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={allegaRiepilogo} onChange={(e) => setAllegaRiepilogo(e.target.checked)} />
            <span>Allega riepilogo extra ospite del periodo (informativo, non fatture separate)</span>
          </label>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,image/*"
              multiple
              className="hidden"
              onChange={(e) => upload(e.target.files)}
            />
            <Button
              disabled={!orgId || uploading}
              onClick={() => fileRef.current?.click()}
              className="rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              Seleziona PDF / immagini
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl shadow-sm">
        <CardHeader className="border-b px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Receipt className="h-4 w-4 text-emerald-600" />
              Fatture struttura
            </CardTitle>
            <select
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs"
              value={statoFilter}
              onChange={(e) => setStatoFilter(e.target.value)}
            >
              <option value="">Tutti gli stati</option>
              <option value="aperte">Aperte (pubblicate / da pagare)</option>
              {Object.entries(STATO_FATTURA_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : !orgId ? (
            <p className="py-8 text-center text-sm text-slate-500">Seleziona una struttura</p>
          ) : filteredItems.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">Nessuna fattura per questa struttura</p>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((d) => {
                const st = STATO_FATTURA_LABELS[d.stato_fattura] || {
                  label: d.stato_fattura || "—",
                  className: "bg-slate-50 text-slate-600 border-slate-200",
                };
                return (
                  <div key={d.id} className="rounded-xl border border-slate-100 bg-white p-3 space-y-2">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-medium text-slate-800">{d.file_name}</span>
                          <Badge variant="outline" className={`text-[10px] ${st.className}`}>{st.label}</Badge>
                          {d.origine === "portale_farmacia" && (
                            <Badge className="bg-slate-100 text-slate-600">Portale</Badge>
                          )}
                          {d.importo_inserito_fattura && (
                            <Badge className="bg-cyan-100 text-cyan-700">In fattura ospite</Badge>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-500">
                          {d.numero_fattura && <span>N. {d.numero_fattura}</span>}
                          {d.importo != null && (
                            <span className="font-medium text-slate-700">€{Number(d.importo).toFixed(2)}</span>
                          )}
                          {d.data_fattura && <span>Data {d.data_fattura}</span>}
                          {d.scadenza && <span>Scad. {d.scadenza}</span>}
                          {d.mese_riferimento && <span>Mese {d.mese_riferimento}</span>}
                          {(d.periodo_da || d.periodo_a) && (
                            <span>Periodo {d.periodo_da || "…"} → {d.periodo_a || "…"}</span>
                          )}
                          {d.created_at && <span>{new Date(d.created_at).toLocaleDateString("it-IT")}</span>}
                        </div>
                        {d.note && <p className="mt-1 text-xs text-slate-400">{d.note}</p>}
                        {(d.riepilogo_extra || []).length > 0 && (
                          <p className="mt-1 text-xs text-emerald-700">
                            Riepilogo extra: {(d.riepilogo_extra || []).length} ospiti
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <select
                          className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs"
                          value={d.stato_fattura || "da_pagare"}
                          onChange={(e) => setStato(d, e.target.value)}
                        >
                          {Object.entries(STATO_FATTURA_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setEditId(editId === d.id ? null : d.id)}>
                          Metadati
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openAudit(d.id)}>
                          <History className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" title="Scarica" onClick={() => downloadAuth(d)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {editId === d.id ? (
                      <EditMeta
                        doc={d}
                        onSave={(body) => saveMeta(d, body)}
                        onCancel={() => setEditId(null)}
                      />
                    ) : null}
                    {auditId === d.id ? (
                      <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        {auditLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                        ) : audit.length === 0 ? (
                          <p>Nessun evento di audit.</p>
                        ) : (
                          <ul className="space-y-1">
                            {audit.map((a) => (
                              <li key={a.id} className="flex flex-wrap gap-x-3">
                                <span className="font-medium text-slate-800">{a.azione}</span>
                                <span>{a.dettaglio || "—"}</span>
                                <span>{a.autore_nome || "—"}</span>
                                <span className="text-slate-400">{formatWhen(a.created_at)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EditMeta({ doc, onSave, onCancel }) {
  const [numero, setNumero] = useState(doc.numero_fattura || "");
  const [dataFattura, setDataFattura] = useState(doc.data_fattura || "");
  const [periodoDa, setPeriodoDa] = useState(doc.periodo_da || "");
  const [periodoA, setPeriodoA] = useState(doc.periodo_a || "");
  const [importo, setImporto] = useState(doc.importo != null ? String(doc.importo) : "");
  const [scadenza, setScadenza] = useState(doc.scadenza || "");
  const [mese, setMese] = useState(doc.mese_riferimento || "");
  const [note, setNote] = useState(doc.note || "");
  const [riepilogo, setRiepilogo] = useState(false);

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg bg-emerald-50/50 p-3">
      <div>
        <label className="text-[10px] uppercase text-slate-500">Numero</label>
        <Input className="mt-1 h-8 w-28" value={numero} onChange={(e) => setNumero(e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-slate-500">Data</label>
        <Input type="date" className="mt-1 h-8" value={dataFattura} onChange={(e) => setDataFattura(e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-slate-500">Importo</label>
        <Input className="mt-1 h-8 w-24" value={importo} onChange={(e) => setImporto(e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-slate-500">Scadenza</label>
        <Input type="date" className="mt-1 h-8" value={scadenza} onChange={(e) => setScadenza(e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-slate-500">Mese</label>
        <Input type="month" className="mt-1 h-8" value={mese} onChange={(e) => setMese(e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-slate-500">Periodo da</label>
        <Input type="date" className="mt-1 h-8" value={periodoDa} onChange={(e) => setPeriodoDa(e.target.value)} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-slate-500">Periodo a</label>
        <Input type="date" className="mt-1 h-8" value={periodoA} onChange={(e) => setPeriodoA(e.target.value)} />
      </div>
      <div className="min-w-[140px] flex-1">
        <label className="text-[10px] uppercase text-slate-500">Note</label>
        <Input className="mt-1 h-8" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <label className="flex items-center gap-1 text-xs text-slate-600">
        <input type="checkbox" checked={riepilogo} onChange={(e) => setRiepilogo(e.target.checked)} />
        Aggiorna riepilogo extra
      </label>
      <Button
        size="sm"
        onClick={() =>
          onSave({
            numero_fattura: numero,
            data_fattura: dataFattura || null,
            periodo_da: periodoDa || null,
            periodo_a: periodoA || null,
            scadenza: scadenza || null,
            mese_riferimento: mese || null,
            note,
            importo: importo === "" ? undefined : Number(String(importo).replace(",", ".")),
            allega_riepilogo_extra: riepilogo || undefined,
          })
        }
      >
        Salva
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>Annulla</Button>
    </div>
  );
}
